import { routes as authRoutes } from "../lib/modules/auth/auth-routes.js";
import { routes as accountRoutes } from "../lib/modules/account/account-routes.js";
import { routes as errorRoutes } from "../lib/pages/error-page-routes.js";
import { createClassModuleFactory, createDataModelModuleFactory } from "../lib/ui/factories/module-factory-helpers.js";

const welcomePageLoader = () => import('./pages/WelcomePage.js');
const helloPageLoader = () => import('./pages/HelloPage.js');
const workOverviewModuleLoader = () => import('./modules/work/WorkOverviewModule.js');
const dailyWorkModuleLoader = () => import('./modules/work/DailyWorkEditor.js');
const monthlyWorkEditorModuleLoader = () => import('./modules/work/MonthlyWorkEditor.js');
const workEventEditorModuleLoader = () => import('./modules/work/WorkEventEditor.js');
const fileRepositoryModuleLoader = () => import('./modules/core/FileRepositoryBrowser.js');
const testPageLoader = () => import('./modules/test/TestPage');
const testCssTypographyLoader = () => import('./modules/test/TestCssTypography');
const testCssGridLoader = () => import('./modules/test/TestCssGrid');
const testCssInputsLoader = () => import('./modules/test/TestCssInputs');

const welcomePageFactory = createClassModuleFactory(welcomePageLoader, 'WelcomePage');
const helloPageFactory = createClassModuleFactory(helloPageLoader, 'HelloPage');
const workOverviewModuleFactory = createClassModuleFactory(workOverviewModuleLoader, 'WorkOverviewModule');
const dailyWorkModuleFactory = createClassModuleFactory(dailyWorkModuleLoader, 'DailyWorkEditor');
const monthlyWorkEditorModuleFactory = createClassModuleFactory(monthlyWorkEditorModuleLoader, 'MonthlyWorkEditor');
const workEventEditorModuleFactory = createClassModuleFactory(workEventEditorModuleLoader, 'WorkEventEditor');
const employeeBrowserModuleFactory = createClassModuleFactory(() => import('./modules/employees/EmployeeBrowser.js'), 'EmployeeBrowser');
const customerBrowserModuleFactory = createClassModuleFactory(() => import('./modules/customers/CustomerBrowser.js'), 'CustomerBrowser');
const machineBrowserModuleFactory = createClassModuleFactory(() => import('./modules/machines/MachineBrowser.js'), 'MachineBrowser');
const fileRepositoryModuleFactory = createClassModuleFactory(fileRepositoryModuleLoader, 'FileRepositoryBrowser');
const testPageFactory = createClassModuleFactory(testPageLoader, 'TestPage');
const testCssTypographyFactory = createClassModuleFactory(testCssTypographyLoader, 'TestCssTypography');
const testCssGridFactory = createClassModuleFactory(testCssGridLoader, 'TestCssGrid');
const testCssInputsFactory = createClassModuleFactory(testCssInputsLoader, 'TestCssInputs');

const machineEditorFactory = createDataModelModuleFactory(
    () => import('../lib/ui/modules/DataEditor.js'),
    () => import('./modules/machines/models/MachineModel.js'),
    'DataEditor'
);
const customerEditorFactory = createDataModelModuleFactory(
    () => import('../lib/ui/modules/DataEditor.js'),
    () => import('./modules/customers/models/CustomerModel.js'),
    'DataEditor'
);
const employeeEditorFactory = createDataModelModuleFactory(
    () => import('../lib/ui/modules/DataEditor.js'),
    () => import('./modules/employees/models/EmployeeModel.js'),
    'DataEditor'
);

export const defaultRoutes = {
    defaultRouteName: 'hello-page',
    loginRouteName: 'auth-login',
    notFoundRouteName: 'error-not-found',
    unauthorizedRouteName: 'error-unauthorized',
    forbiddenRouteName: 'error-forbidden',
};

export const routes = [

    ...authRoutes,
    ...accountRoutes,
    ...errorRoutes,

    // public

    { name: 'welcome-page', path: '/welcome-page', factory: welcomePageFactory, public: true },

    // protected

    { name: 'hello-page', path: '/hello-page', factory: helloPageFactory, default: false },

    // Work

    { name: 'work-overview-by-day', path: '/work/overview/day/:day', factory: workOverviewModuleFactory, privileges: ['manageWork', 'adminWork'] },
    { name: 'work-overview', path: '/work/overview', factory: workOverviewModuleFactory, privileges: ['manageWork', 'adminWork'], default: true },

    { name: 'daily-work-by-day-and-employee', path: '/work/day/:day/employee/:employee', factory: dailyWorkModuleFactory, privileges: ['editWork', 'manageWork', 'adminWork'] },
    { name: 'daily-work-by-day', path: '/work/day/:day', factory: dailyWorkModuleFactory, privileges: ['editWork', 'manageWork', 'adminWork'] },
    { name: 'daily-work', path: '/work/day', factory: dailyWorkModuleFactory, privileges: ['editWork', 'manageWork', 'adminWork'], default: true },

    { name: 'monthly-work-by-month-and-employee', path: '/work/month/:month/employee/:employee', factory: monthlyWorkEditorModuleFactory, privileges: ['editWork', 'manageWork', 'adminWork'] },
    { name: 'monthly-work-by-month', path: '/work/month/:month', factory: monthlyWorkEditorModuleFactory, privileges: ['editWork', 'manageWork', 'adminWork'] },
    { name: 'monthly-work', path: '/work/month', factory: monthlyWorkEditorModuleFactory, privileges: ['editWork', 'manageWork', 'adminWork'] },

    { name: 'work-event-editor', path: '/work-event/:id', factory: workEventEditorModuleFactory, privileges: ['editWork', 'manageWork', 'adminWork'] },

    // Machines

    { name: 'machine-browser', path: '/machines/machine', factory: machineBrowserModuleFactory, privileges: ['editMachines', 'manageMachines', 'adminMachines'], options: { entityName: 'machines.Machine', browserRoute: 'machine-browser', editorRoute: 'machine-editor' }, },
    { name: 'machine-editor', path: '/machines/machine/:id', factory: machineEditorFactory, privileges: ['editMachines', 'manageMachines', 'adminMachines'], options: { entityName: 'machines.Machine', browserRoute: 'machine-browser', editorRoute: 'machine-editor' }, },

    // Customers

    { name: 'customer-browser', path: '/customers/customer', factory: customerBrowserModuleFactory, privileges: ['editCustomers', 'manageCustomers', 'adminCustomers'], options: { entityName: 'customers.Customer', browserRoute: 'customer-browser', editorRoute: 'customer-editor' }, },
    { name: 'customer-editor', path: '/customers/customer/:id', factory: customerEditorFactory, privileges: ['editCustomers', 'manageCustomers', 'adminCustomers'], options: { entityName: 'customers.Customer', browserRoute: 'customer-browser', editorRoute: 'customer-editor' }, },

    // Employees

    { name: 'employee-browser', path: '/employees/employee', factory: employeeBrowserModuleFactory, privileges: ['editEmployees', 'manageEmployees', 'adminEmployees'], options: { entityName: 'employees.Employee', browserRoute: 'employee-browser', editorRoute: 'employee-editor' }, },
    { name: 'employee-editor', path: '/employees/employee/:id', factory: employeeEditorFactory, privileges: ['editEmployees', 'manageEmployees', 'adminEmployees'], options: { entityName: 'employees.Employee', browserRoute: 'employee-browser', editorRoute: 'employee-editor' }, },

    // File Repository

    { name: 'file-repository-folder', path: '/file-repository/folder/:uid', factory: fileRepositoryModuleFactory, privileges: ['useFileRepository', 'adminFileRepository'] },
    { name: 'file-repository', path: '/file-repository', factory: fileRepositoryModuleFactory, privileges: ['useFileRepository', 'adminFileRepository'] },

    // Reports

    { name: 'employee-monthly-work-report', path: '/reports/work/monthly/employee/:employee/month/:month', privileges: ['editWork', 'manageWork', 'adminWork'] },

    // Test

    { name: 'test-page', path: '/test-page', factory: testPageFactory },
    { name: 'test-css-typography', path: '/test-css-typography', factory: testCssTypographyFactory },
    { name: 'test-css-grid', path: '/test-css-grid', factory: testCssGridFactory },
    { name: 'test-css-inputs', path: '/test-css-inputs', factory: testCssInputsFactory },

];
